import React, { useEffect } from "react";
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaHeartbeat,
  FaShieldAlt,
  FaUserMd,
} from "react-icons/fa";

/* CARD */
const Card = ({ title, children, icon }) => (
  <div
    className="
      group
      bg-white/90 backdrop-blur-sm
      border border-red-100
      rounded-2xl
      p-5
      shadow-sm
      hover:shadow-xl
      hover:-translate-y-1
      transition-all duration-300
    "
  >
    {/* ICON */}
    {icon && (
      <div
        className="
          w-12 h-12
          rounded-2xl
          bg-gradient-to-br from-red-50 to-red-100
          text-red-600
          flex items-center justify-center
          mb-4
          group-hover:from-red-500
          group-hover:to-red-600
          group-hover:text-white
          transition-all duration-300
        "
      >
        {icon}
      </div>
    )}

    {/* TITLE */}
    {title && (
      <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">
        {title}
      </h2>
    )}

    {/* CONTENT */}
    <div className="text-gray-600 leading-relaxed text-sm md:text-base">
      {children}
    </div>
  </div>
);

export default function AboutUs() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50">

      {/* HERO SECTION */}
      <div
        className="
          w-full
          bg-gradient-to-r from-red-600 to-red-500
          text-white
          py-10 md:py-12
          px-4 md:px-10
          text-center
        "
      >
        <h1 className="text-2xl md:text-4xl font-bold mb-2">
          About MedrphaLab
        </h1>

        <p
          className="
            max-w-2xl mx-auto
            text-xs md:text-base
            opacity-90
            leading-relaxed
          "
        >
          Simplifying healthcare access by connecting users with trusted
          diagnostic laboratories across India.
        </p>
      </div>

      {/* CONTENT */}
      <div className="px-4 md:px-10 py-6 md:py-8">

        {/* TOP GRID */}
        <div className="grid gap-4 md:grid-cols-2">

          {/* INTRO */}
          <Card icon={<FaHeartbeat size={20} />}>
            <p>
              <strong>MedrphaLab</strong> is a healthcare technology platform
              operated by <strong>ARMC PHARMA PRIVATE LIMITED</strong>.
              We simplify diagnostic services by connecting users with
              trusted laboratories across India.
            </p>
          </Card>

          {/* MISSION */}
          <Card
            title="Our Mission"
            icon={<FaShieldAlt size={20} />}
          >
            <p>
              Our mission is to make healthcare services accessible,
              affordable, and reliable through seamless digital healthcare
              experiences.
            </p>
          </Card>

          {/* SERVICES */}
          <Card
            title="What We Do"
            icon={<FaUserMd size={20} />}
          >
            <ul className="list-disc pl-5 space-y-1">
              <li>Discover nearby diagnostic laboratories</li>
              <li>Book lab tests and health packages</li>
              <li>Schedule home sample collection</li>
              <li>Access digital reports anytime</li>
              <li>Convenient healthcare services</li>
            </ul>
          </Card>

          {/* NOTE */}
          <div
            className="
              bg-gradient-to-br from-yellow-50 to-white
              border border-yellow-200
              rounded-2xl
              p-5
              shadow-sm
              hover:shadow-lg
              transition-all duration-300
            "
          >
            <div
              className="
                w-12 h-12
                rounded-2xl
                bg-yellow-100
                text-yellow-600
                flex items-center justify-center
                mb-4
              "
            >
              ⚠️
            </div>

            <h2 className="text-lg md:text-xl font-semibold text-yellow-700 mb-2">
              Important Note
            </h2>

            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              MedrphaLab connects users with independent laboratories.
              We do not conduct medical tests or provide direct medical advice.
            </p>
          </div>

        </div>

        {/* CONTACT SECTION */}
        <div
          className="
            mt-6
            bg-white
            border border-red-100
            rounded-2xl
            p-5 md:p-6
            shadow-sm
          "
        >
          <h2 className="text-lg md:text-xl font-semibold text-red-600 mb-5">
            Contact Information
          </h2>

          <div className="grid gap-4 md:grid-cols-3">

            {/* ADDRESS */}
            <div
              className="
                flex items-start gap-3
                bg-gradient-to-br from-red-50 to-white
                border border-red-100
                rounded-2xl
                p-4
                hover:shadow-md
                transition-all duration-300
              "
            >
              <div
                className="
                  w-10 h-10 rounded-xl
                  bg-red-100 text-red-600
                  flex items-center justify-center
                  flex-shrink-0
                "
              >
                <FaMapMarkerAlt />
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                ARMC PHARMA PRIVATE LIMITED <br />
                D-43 1st Floor Sector 6 <br />
                Noida 201301
              </p>
            </div>

            {/* PHONE */}
            <div
              className="
                flex items-center gap-3
                bg-gradient-to-br from-red-50 to-white
                border border-red-100
                rounded-2xl
                p-4
                hover:shadow-md
                transition-all duration-300
              "
            >
              <div
                className="
                  w-10 h-10 rounded-xl
                  bg-red-100 text-red-600
                  flex items-center justify-center
                  flex-shrink-0
                "
              >
                <FaPhone />
              </div>

              <p className="text-sm text-gray-600">
                +91 8178399885
              </p>
            </div>

            {/* EMAIL */}
            <div
              className="
                flex items-center gap-3
                bg-gradient-to-br from-red-50 to-white
                border border-red-100
                rounded-2xl
                p-4
                hover:shadow-md
                transition-all duration-300
              "
            >
              <div
                className="
                  w-10 h-10 rounded-xl
                  bg-red-100 text-red-600
                  flex items-center justify-center
                  flex-shrink-0
                "
              >
                <FaEnvelope />
              </div>

              <p className="text-sm text-gray-600 break-all">
                info@medrpha.com
              </p>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}